import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { User } from "@shared/schema";
import axios from "axios";
import { setSentryUser } from "@/lib/sentry";
// net.ts owns the token (Keychain in the app, localStorage on the web). ALIASED imports: this
// file's own `const [token, setToken] = useState(...)` would shadow net's setToken.
import { classifyProfileStatus, decideProfileFailure, tokenNeedsRefresh } from "@/lib/sessionRecovery";
import { getToken as readToken, setToken as persistToken, clearToken as wipeToken, flushToken, apiUrl, credentialsMode, isAppBuild, tokenScope } from "@/lib/net";
import { pushBridge } from "@/lib/pushBridge";
import { onSessionUnauthorized, shouldRecheckSession } from "@/lib/sessionWatch";

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string, user?: User) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Audit 2026-09-27 M6 — ONE refresh in flight per token (profile 401 + focus + interval
 * can all fire together). `unauthorized` = the server refused (sign in again);
 * `failed` = 5xx / network, which must NOT end the session.
 */
type RefreshOutcome = { token: string } | { unauthorized: true } | { failed: true };
let refreshInflight: { from: string; p: Promise<RefreshOutcome> } | null = null;
function refreshSessionOnce(current: string): Promise<RefreshOutcome> {
  if (refreshInflight && refreshInflight.from === current) return refreshInflight.p;
  const p = (async (): Promise<RefreshOutcome> => {
    try {
      const r = await fetch(apiUrl("/api/auth/refresh-token"), {
        method: "POST",
        headers: { Authorization: `Bearer ${current}` },
        credentials: credentialsMode(),
      });
      if (r.ok) {
        const data = await r.json();
        if (typeof data?.token === "string" && data.token) return { token: data.token };
        return { failed: true };
      }
      return r.status === 401 ? { unauthorized: true } : { failed: true };
    } catch {
      return { failed: true };
    }
  })().finally(() => { if (refreshInflight?.p === p) refreshInflight = null; });
  refreshInflight = { from: current, p };
  return p;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    try {
      return readToken();
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(() => {
    // Only start loading if we have a token to validate. A crew-scoped token (store app, Task C4)
    // has no client profile to load — CrewApp validates it against /api/auth/crew/me itself.
    const t = readToken();
    return !!t && tokenScope(t) !== "crew";
  });
  // The token a refresh just produced — a 401 on THAT token ends the session (no loop).
  const refreshedTokenRef = useRef<string | null>(null);

  /** Adopt a refreshed token; the [token] effect re-loads the profile with it. */
  const adoptRefreshedToken = (newToken: string) => {
    refreshedTokenRef.current = newToken;
    persistToken(newToken);
    localStorage.setItem("tokenCreatedAt", Date.now().toString());
    setToken(newToken);
  };

  // Listen for token changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const newToken = readToken();
        if (newToken !== token) {
          setToken(newToken);
        }
      } catch (error) {
        console.error('Failed to read localStorage:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);
    
    // Check immediately on mount
    try {
      const currentToken = readToken();
      if (currentToken !== token) {
        setToken(currentToken);
      }
    } catch (error) {
      console.error('Failed to read localStorage on mount:', error);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, [token]);

  useEffect(() => {
    // 🔴 Never send a CREW token to /api/user/profile: every main-secret verifier rejects it, the
    // 401 would trigger /api/auth/refresh-token (which cannot extend it) and then logout().
    if (token && tokenScope(token) === "crew") {
      setUser(null);
      setIsLoading(false);
    } else if (token) {
      fetchUserProfile();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  // Set Sentry user context when user changes
  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, email: user.email, role: user.role });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    // App with a stored session: a slow venue network must not look like "signed out"
    // (the app gate would bounce the couple to /login and drop the deep link). At 5 s show
    // the last-known user if there is one; otherwise keep the loader until the profile load
    // settles, with a hard cap so a hung request can never pin the splash forever.
    if (isAppBuild() && token) {
      if (!isLoading) return;
      const soft = setTimeout(() => {
        if (restoreCachedUser()) setIsLoading(false);
      }, 5000);
      const hard = setTimeout(() => setIsLoading(false), 30000);
      return () => { clearTimeout(soft); clearTimeout(hard); };
    }
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log('⏰ Loading timeout reached, forcing loading to false');
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [isLoading, token]);

  /** App only: the user saved at the last successful login/profile load, if any. */
  const restoreCachedUser = (): boolean => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) { setUser(JSON.parse(raw) as User); return true; }
    } catch { /* corrupt cache — stay signed-out-looking; the token is kept */ }
    return false;
  };

  const fetchUserProfile = async () => {
    const current = token;
    let handedOff = false;
    try {
      const response = await fetch(apiUrl("/api/user/profile"), {
        headers: {
          Authorization: `Bearer ${current}`,
        },
        credentials: credentialsMode(),
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Remember who is signed in so a server blip (web) or an offline launch (app)
        // can still show the portal.
        try { localStorage.setItem("user", JSON.stringify(userData)); } catch { /* ignore */ }
        return;
      }

      // Audit 2026-09-27 M6: only a 401 that survives ONE refresh ends the session. A
      // 5xx / proxy blip keeps the token and the last-known user (web and app alike).
      const action = decideProfileFailure(
        classifyProfileStatus(response.status),
        !!current && refreshedTokenRef.current === current,
      );
      if (action === 'refresh' && current) {
        const outcome = await refreshSessionOnce(current);
        if ('token' in outcome) {
          handedOff = true; // stay "loading" — the [token] effect reloads the profile
          adoptRefreshedToken(outcome.token);
        } else if ('unauthorized' in outcome) {
          logout();
        } else {
          restoreCachedUser();
        }
      } else if (action === 'logout') {
        logout();
      } else {
        restoreCachedUser();
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      // A thrown fetch is offline / a network blip — keep the stored session and show the
      // last-known user (web and app alike; audit M6).
      restoreCachedUser();
    } finally {
      if (!handedOff) setIsLoading(false);
    }
  };

  // Audit R6-9: a session that ends WHILE the portal is open (expiry, the 30-day cap, a password
  // change elsewhere) made every screen say "Check your connection". A 401 from any of our APIs on
  // the current token re-runs the start-up check above: profile → one refresh → sign-out only if the
  // server refuses both (lib/sessionWatch.ts).
  const recheckRef = useRef<() => void>(() => {});
  recheckRef.current = () => { void fetchUserProfile(); };
  const lastRecheckRef = useRef<{ token: string; at: number } | null>(null);
  useEffect(() => onSessionUnauthorized((sent) => {
    const current = readToken();
    const now = Date.now();
    if (!shouldRecheckSession(sent, current, !!current && tokenScope(current) === "crew", lastRecheckRef.current, now)) return;
    lastRecheckRef.current = { token: current!, at: now };
    recheckRef.current();
  }), []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(apiUrl("/api/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const data = await response.json();

      setToken(data.token);
      setUser(data.user);
      persistToken(data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("tokenCreatedAt", Date.now().toString());
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const loginWithToken = (newToken: string, userData?: User) => {
    setToken(newToken);
    persistToken(newToken);
    localStorage.setItem("tokenCreatedAt", Date.now().toString());
    if (tokenScope(newToken) === "crew") {
      // Crew sign-in (Task C4): no client profile; CrewApp owns the crew session.
      setUser(null);
      localStorage.removeItem("user");
      setIsLoading(false);
    } else if (userData) {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      // Fetch user profile with the new token
      fetchUserProfile();
    }
  };

  const logout = async () => {
    // Task D6 Step 4 — sign-out order: unregister THIS device's push token (DELETE, needs the token)
    // → clear the token → flush → navigate. No-op on the web. Never blocks the sign-out.
    await pushBridge().unregister().catch(() => {});
    try {
      // Call backend logout endpoint to clear server sessions
      await fetch(apiUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: credentialsMode()
      });
    } catch (error) {
      console.error('Server logout failed:', error);
    }

    // CRITICAL: Clear all authentication state completely
    setUser(null);
    setToken(null);
    setIsLoading(false); // Ensure loading state is cleared
    wipeToken();
    localStorage.removeItem("user");
    localStorage.removeItem("tokenCreatedAt");

    // Clear browser session storage as well
    sessionStorage.clear();

    // Force clear authentication headers
    delete axios.defaults.headers.common['Authorization'];

    // Clear any cached query data that might contain user info
    queryClient.clear();

    // Keychain/Keystore writes are async in the app: let the wipe land before any
    // caller navigates the document away (no-op on the web).
    await flushToken();
  };

  // CRITICAL FIX: Auto-refresh token every 6 days to prevent expiry
  // Token expires after 7 days, so refresh at 6 days to ensure continuity
  useEffect(() => {
    // A crew token is refreshed by CrewApp through /api/auth/crew/refresh — never here.
    if (!token || tokenScope(token) === "crew") return;

    const refreshToken = async () => {
      const outcome = await refreshSessionOnce(token);
      if ('token' in outcome) {
        adoptRefreshedToken(outcome.token);
      } else if ('unauthorized' in outcome) {
        // Only a real 401 ends the session (a 5xx/proxy blip must not log the couple out).
        logout();
      } else {
        console.error('Session auto-refresh failed (kept the session; will retry)');
      }
    };

    // Refresh every 6 days (token expires in 7 days)
    const refreshInterval = setInterval(refreshToken, 6 * 24 * 60 * 60 * 1000);

    // Also refresh on focus when the token is within 2 days of expiry (read from the
    // token's own `exp`, not a localStorage timestamp that other tabs/apps never set).
    const handleFocus = async () => {
      if (tokenNeedsRefresh(token)) await refreshToken();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
